import { useCallback, useEffect, useMemo, useState } from 'react';
import { Filter, getEmptyFilterExpression } from '@fabric-ui/components';
import { AutomationRuleCreateDto, AutomationRuleDto, automationRuleService } from '../services/automationRuleService';
import { catalogService, ConditionCatalogDto, TriggerEvent } from '../services/catalogService';
import { AutomationRule } from '../types';
import { backendToFrontendFilter, frontendToBackendFilter } from '../utils/filterTransform';

export interface ConditionField {
  id: string;
  label: string;
  type: 'text' | 'select';
  options?: string[];
}

const STORAGE_KEY = 'soar_new_automation_rule_editing';

export interface NewAutomationRuleState {
  editingRuleId: string | null;
  currentRuleToLoad: AutomationRule | null;
  name: string;
  description: string;
  entityType: string;
  triggerType: string;
  selectedPlaybookIds: string[];
  isActive: boolean;
  filters: Filter;
  updateFilters: Filter;
  events: TriggerEvent[];
  conditions: ConditionCatalogDto[];
  isLoadingEvents: boolean;
  isLoadingConditions: boolean;
  isSaving: boolean;
}

export const useNewAutomationRule = (onRuleSaved?: (rule: AutomationRule, isNew: boolean) => void) => {
  const initialState: NewAutomationRuleState = {
    editingRuleId: null,
    currentRuleToLoad: null,
    name: '',
    description: '',
    entityType: '',
    triggerType: '',
    selectedPlaybookIds: [],
    isActive: true,
    filters: getEmptyFilterExpression(),
    updateFilters: getEmptyFilterExpression(),
    events: [],
    conditions: [],
    isLoadingEvents: false,
    isLoadingConditions: false,
    isSaving: false,
  };

  const [state, setState] = useState<NewAutomationRuleState>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return { ...initialState, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Failed to load new rule state', e);
      }
    }
    return initialState;
  });

  const persist = useCallback((newState: Partial<NewAutomationRuleState>) => {
    setState(prev => {
      const updated = { ...prev, ...newState };
      const toPersist = {
        editingRuleId: updated.editingRuleId,
        name: updated.name,
        description: updated.description,
        entityType: updated.entityType,
        triggerType: updated.triggerType,
        selectedPlaybookIds: updated.selectedPlaybookIds,
        isActive: updated.isActive,
        filters: updated.filters,
        updateFilters: updated.updateFilters,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
      return updated;
    });
  }, []);

  const fetchEvents = useCallback(async (entityId: string) => {
    if (!entityId) return;
    setState(prev => ({ ...prev, isLoadingEvents: true }));
    try {
      const data = await catalogService.getEvents(entityId);
      setState(prev => ({ ...prev, events: Array.isArray(data) ? data : [], isLoadingEvents: false }));
    } catch (error) {
      console.error(`Failed to fetch events for entity ${entityId}`, error);
      setState(prev => ({ ...prev, isLoadingEvents: false }));
    }
  }, []);

  const fetchConditions = useCallback(async (entityId: string) => {
    if (!entityId) return;
    setState(prev => ({ ...prev, isLoadingConditions: true }));
    try {
      const data = await catalogService.getConditions(entityId);
      setState(prev => ({ ...prev, conditions: Array.isArray(data) ? data : [], isLoadingConditions: false }));
    } catch (error) {
      console.error(`Failed to fetch conditions for entity ${entityId}`, error);
      setState(prev => ({ ...prev, isLoadingConditions: false }));
    }
  }, []);

  useEffect(() => {
    if (state.entityType) {
      fetchEvents(state.entityType);
      fetchConditions(state.entityType);
    }
  }, [state.entityType, fetchEvents, fetchConditions]);

  // Transform and load filters when conditions or the rule to load changes
  useEffect(() => {
    if (state.currentRuleToLoad) {
      const rule = state.currentRuleToLoad;

      const isUpdateNode = (node: any): boolean => {
        if (!node) return false;
        if (node.operator === 'CHANGE') return true;
        if (Array.isArray(node.rules)) return node.rules.some(isUpdateNode);
        return false;
      };

      const splitRules = (rawFilter: any) => {
        if (!rawFilter || !Array.isArray(rawFilter.rules)) {
          return { conditionRules: [], updateRules: [] };
        }

        const conditionRules = rawFilter.rules.filter((r: any) => !isUpdateNode(r));
        const updateRules = rawFilter.rules.filter((r: any) => isUpdateNode(r));
        return { conditionRules, updateRules };
      };

      const { conditionRules, updateRules } = splitRules(rule.rawFilter);

      const transformedFilters =
        rule.filters || backendToFrontendFilter({ rules: conditionRules, operator: rule.rawFilter?.operator || 'AND' }, state.conditions);

      const transformedUpdateFilters =
        updateRules.length > 0
          ? backendToFrontendFilter({ rules: updateRules, operator: 'AND' }, state.conditions)
          : getEmptyFilterExpression();

      persist({
        filters: transformedFilters,
        updateFilters: transformedUpdateFilters,
        // Reset the trigger to avoid infinite loop
        currentRuleToLoad: null,
      });
    }
  }, [state.currentRuleToLoad, state.conditions, persist]);

  const setName = useCallback((name: string) => persist({ name }), [persist]);
  const setIsActive = useCallback((isActive: boolean) => persist({ isActive }), [persist]);
  const setDescription = useCallback((description: string) => persist({ description }), [persist]);

  const setEntityType = useCallback(
    (entityType: string) => {
      persist({
        entityType,
        triggerType: '',
        filters: getEmptyFilterExpression(),
        updateFilters: getEmptyFilterExpression(),
      });
    },
    [persist]
  );

  const setTriggerType = useCallback((triggerType: string) => persist({ triggerType }), [persist]);

  const setFilters = useCallback((filters: Filter) => persist({ filters }), [persist]);

  const setUpdateFilters = useCallback((updateFilters: Filter) => persist({ updateFilters }), [persist]);

  const togglePlaybook = useCallback(
    async (playbookId: string) => {
      const isSelected = state.selectedPlaybookIds.includes(playbookId);
      try {
        if (state.editingRuleId) {
          if (isSelected) {
            await automationRuleService.removePlaybookFromRule(state.editingRuleId, playbookId);
          } else {
            await automationRuleService.addPlaybookToRule(state.editingRuleId, playbookId);
          }
        }

        persist({
          selectedPlaybookIds: isSelected
            ? state.selectedPlaybookIds.filter(id => id !== playbookId)
            : [...state.selectedPlaybookIds, playbookId],
        });
      } catch (error) {
        console.error('Failed to update playbook association', error);
      }
    },
    [state.editingRuleId, state.selectedPlaybookIds, persist]
  );

  const removePlaybook = useCallback(
    async (playbookId: string) => {
      try {
        if (state.editingRuleId) {
          await automationRuleService.removePlaybookFromRule(state.editingRuleId, playbookId);
        }
        persist({
          selectedPlaybookIds: state.selectedPlaybookIds.filter(id => id !== playbookId),
        });
      } catch (error) {
        console.error('Failed to remove playbook association', error);
      }
    },
    [state.editingRuleId, state.selectedPlaybookIds, persist]
  );

  const reorderPlaybooks = useCallback(
    (startIndex: number, endIndex: number) => {
      const result = Array.from(state.selectedPlaybookIds);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      persist({ selectedPlaybookIds: result });
    },
    [state.selectedPlaybookIds, persist]
  );

  const resetForm = useCallback(() => {
    const resetState: Partial<NewAutomationRuleState> = {
      editingRuleId: null,
      name: '',
      description: '',
      entityType: '',
      triggerType: '',
      selectedPlaybookIds: [],
      isActive: true,
      filters: getEmptyFilterExpression(),
      updateFilters: getEmptyFilterExpression(),
      events: [],
      conditions: [],
    };
    persist(resetState);
  }, [persist]);

  const loadRule = useCallback(
    (rule: AutomationRule) => {
      persist({
        editingRuleId: rule.id,
        currentRuleToLoad: rule, // Trigger the transformation useEffect
        name: rule.name || '',
        description: rule.description || '',
        entityType: rule.entityType || '',
        triggerType: rule.triggerType || '',
        selectedPlaybookIds: [...(rule.playbookIds || [])],
        isActive: rule.status === 'Active',
      });
    },
    [persist]
  );

  const isValid = useMemo(
    () =>
      (state.name?.trim()?.length || 0) > 0 && (state.entityType?.trim()?.length || 0) > 0 && (state.triggerType?.trim()?.length || 0) > 0,
    [state.name, state.entityType, state.triggerType]
  );

  const saveAutomationRule = useCallback(async () => {
    if (!isValid) return null;

    setState(prev => ({ ...prev, isSaving: true }));

    // Combine both logical builders into a single logical AND structure
    const combinedFrontendFilter: Filter = {
      and: {
        operands: [state.filters, state.updateFilters],
      },
    };

    const ruleData: AutomationRuleCreateDto = {
      name: state.name,
      description: state.description,
      active: state.isActive,
      triggerEntity: state.entityType,
      triggerEvent: state.triggerType,
      playbookIds: [...state.selectedPlaybookIds],
      filter: frontendToBackendFilter(combinedFrontendFilter),
    };

    try {
      const isNew = !state.editingRuleId;
      const savedRule: AutomationRule = await (async () => {
        if (state.editingRuleId) {
          const updateDto: Partial<AutomationRuleDto> = {
            name: state.name,
            description: state.description,
            active: state.isActive,
            triggerEntity: state.entityType,
            triggerEvent: state.triggerType,
            filter: ruleData.filter,
          };
          await automationRuleService.updateAutomationRule(state.editingRuleId, updateDto);

          return {
            id: state.editingRuleId,
            name: state.name,
            description: state.description,
            status: state.isActive ? 'Active' : 'Inactive',
            lastModified: new Date().toISOString(),
            entityType: state.entityType,
            triggerType: state.triggerType,
            playbookIds: [...state.selectedPlaybookIds],
            filters: state.filters,
            updateFilters: state.updateFilters,
            rawFilter: ruleData.filter,
          };
        }
        const createdRule = await automationRuleService.createAutomationRule(ruleData);

        return {
          id: createdRule.ruleId,
          name: createdRule.name,
          description: createdRule.description,
          status: createdRule.active ? 'Active' : 'Inactive',
          lastModified: createdRule.updatedAt || createdRule.createdAt || new Date().toISOString(),
          entityType: createdRule.triggerEntity,
          triggerType: createdRule.triggerEvent,
          playbookIds: createdRule.playbooks?.map(p => p.playbookId) || [],
          filters: state.filters,
          updateFilters: state.updateFilters,
          rawFilter: createdRule.filter,
        };
      })();

      if (onRuleSaved) {
        onRuleSaved(savedRule, isNew);
      }

      setState(prev => ({ ...prev, isSaving: false }));
      return savedRule;
    } catch (error) {
      console.error('Failed to save automation rule', error);
      setState(prev => ({ ...prev, isSaving: false }));
      return null;
    }
  }, [isValid, state, onRuleSaved]);

  const triggerOptions = useMemo(() => state.events.map(e => ({ title: e.eventName || e.eventId, value: e.eventId })), [state.events]);

  const availableConditionFields = useMemo(
    (): ConditionField[] =>
      state.conditions.map(c => ({
        id: c.fieldKey,
        label: c.fieldKey.split('.').pop() || c.fieldKey,
        type: c.fieldType.toLowerCase() === 'enum' ? 'select' : 'text',
      })),
    [state.conditions]
  );

  return {
    ...state,
    isValid,
    triggerOptions,
    availableConditionFields,
    setName,
    setIsActive,
    setDescription,
    setEntityType,
    setTriggerType,
    setFilters,
    setUpdateFilters,
    togglePlaybook,
    removePlaybook,
    reorderPlaybooks,
    saveAutomationRule,
    loadRule,
    resetForm,
  };
};
